import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectOption } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { Input } from './ui/input';
import { ArrowLeftRight, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import api from '../services/api';
import dayjs from 'dayjs';

const statusColors = {
  assigned: 'info', delivered: 'success', failed: 'destructive', transferred: 'warning',
  postponed: 'warning', tomorrow: 'warning', phone_not_answered: 'warning',
  cannot_contact: 'warning', wrong_phone: 'destructive', unclear_address: 'destructive',
  gate_locked: 'warning', entrance_chip: 'warning', other: 'secondary', no_one_home: 'destructive',
  returned: 'destructive',
};
const statusLabels = {
  assigned: 'Хүргэлтэнд гарсан', delivered: 'Хүргэгдсэн', failed: 'Цуцалсан', transferred: 'Шилжүүлсэн',
  postponed: 'Хойшлогдсон', tomorrow: 'Маргааш авах', phone_not_answered: 'Утас авахгүй',
  cannot_contact: 'Холбогдох боломжгүй', wrong_phone: 'Утасны дугаар буруу', unclear_address: 'Хаяг тодорхойгүй',
  gate_locked: 'Хотхоны хаалт', entrance_chip: 'Орц байрны чип', other: 'Бусад', no_one_home: 'Хаяг хүнгүй',
  returned: 'Буцаасан',
};

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({ startDate: today, endDate: today });
  const [transferModal, setTransferModal] = useState({ open: false, deliveryId: null });
  const [transferReason, setTransferReason] = useState('');
  const [transferAddress, setTransferAddress] = useState('');
  const [transferDistrict, setTransferDistrict] = useState('');
  const [detailModal, setDetailModal] = useState({ open: false, delivery: null });
  const [editModal, setEditModal] = useState({ open: false, delivery: null });
  const [editForm, setEditForm] = useState({});
  const { toast } = useToast();

  const fetchDeliveries = async (p = page, f = filters) => {
    setLoading(true);
    try {
      const res = await api.get('/deliveries', { params: { page: p, limit: 50, ...f } });
      setDeliveries(res.data.deliveries);
      setTotal(res.data.total);
    } catch {
      toast({ title: 'Хүргэлт татахад алдаа гарлаа', variant: 'error' });
    }
    setLoading(false);
  };

  const fetchDrivers = async () => {
    try { const res = await api.get('/drivers'); setDrivers(res.data); } catch {}
  };

  const fetchDistricts = async () => {
    try { const res = await api.get('/districts'); setDistricts(res.data); } catch {}
  };

  useEffect(() => { fetchDeliveries(); fetchDrivers(); fetchDistricts(); }, []);

  const handleTransfer = async (toDriverId) => {
    try {
      const body = { toDriverId, reason: transferReason || undefined };
      if (transferAddress.trim()) body.newAddress = transferAddress.trim();
      if (transferDistrict) body.newDistrict = transferDistrict;
      await api.post(`/deliveries/${transferModal.deliveryId}/transfer`, body);
      toast({ title: 'Хүргэлт шилжүүлэгдлээ', variant: 'success' });
      setTransferModal({ open: false, deliveryId: null });
      setTransferReason('');
      setTransferAddress('');
      setTransferDistrict('');
      fetchDeliveries();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/deliveries/${id}/status`, { status });
      toast({ title: 'Төлөв шинэчлэгдлээ', variant: 'success' });
      fetchDeliveries();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const openEditDelivery = (d) => {
    setEditForm({
      driver: d.driver?._id || '',
      note: d.note || '',
      zone: d.zone || '',
      extraKm: d.extraKm || 0,
    });
    setEditModal({ open: true, delivery: d });
  };

  const handleEditDelivery = async () => {
    try {
      const body = {};
      if (editForm.driver) body.driver = editForm.driver;
      if (editForm.note !== undefined) body.note = editForm.note;
      if (editForm.zone !== undefined) body.zone = editForm.zone || undefined;
      if (editForm.extraKm !== undefined) body.extraKm = editForm.extraKm;
      await api.patch(`/deliveries/${editModal.delivery._id}`, body);
      toast({ title: 'Хүргэлт шинэчлэгдлээ', variant: 'success' });
      setEditModal({ open: false, delivery: null });
      setEditForm({});
      fetchDeliveries();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleReturn = async (id) => {
    if (!window.confirm('Энэ хүргэлтийг буцаах уу? (ачаа буруу)')) return;
    try {
      await api.patch(`/deliveries/${id}/status`, { status: 'returned', failReason: 'Ачаа буруу' });
      toast({ title: 'Хүргэлт буцаагдлаа', variant: 'success' });
      fetchDeliveries();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold">Хүргэлтүүд ({total})</h2>
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Нэр, утас, дугаар хайх..." value={filters.search || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, search: v }));
          }} onKeyDown={e => { if (e.key === 'Enter') fetchDeliveries(1, filters); }} className="w-48" />
          <Select value={filters.driver || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, driver: v }));
            fetchDeliveries(1, { ...filters, driver: v });
          }} className="w-36">
            <SelectOption value="">Хүргэлтийн ажилтан</SelectOption>
            {drivers.map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
          </Select>
          <Select value={filters.status || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, status: v }));
            fetchDeliveries(1, { ...filters, status: v });
          }} className="w-40">
            <SelectOption value="">Төлөв</SelectOption>
            {Object.entries(statusLabels).map(([k, v]) => <SelectOption key={k} value={k}>{v}</SelectOption>)}
          </Select>
          <Input type="date" value={filters.startDate || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, startDate: v }));
            fetchDeliveries(1, { ...filters, startDate: v });
          }} className="w-36" title="Эхлэх огноо" />
          <Input type="date" value={filters.endDate || ''} onChange={e => {
            const v = e.target.value || undefined;
            setFilters(f => ({ ...f, endDate: v }));
            fetchDeliveries(1, { ...filters, endDate: v });
          }} className="w-36" title="Дуусах огноо" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Захиалга №</TableHead>
                  <TableHead>Хүлээн авагч</TableHead>
                  <TableHead>Утас</TableHead>
                  <TableHead>Дүүрэг</TableHead>
                  <TableHead>Хүргэлтийн ажилтан</TableHead>
                  <TableHead>Төлөв</TableHead>
                  <TableHead>Шилжүүлэлт</TableHead>
                  <TableHead>Үйлдэл</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map(d => (
                  <TableRow key={d._id}>
                    <TableCell className="font-medium">{d.order?.orderNumber}</TableCell>
                    <TableCell>{d.order?.receiverName}</TableCell>
                    <TableCell>{d.order?.phone1}</TableCell>
                    <TableCell>{d.order?.district?.name}</TableCell>
                    <TableCell>{d.driver?.name}</TableCell>
                    <TableCell><Badge variant={statusColors[d.status]}>{statusLabels[d.status]}</Badge></TableCell>
                    <TableCell>{d.transfers?.length > 0 ? <Badge variant="warning">{d.transfers.length}x</Badge> : '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setDetailModal({ open: true, delivery: d })}>Дэлгэрэнгүй</Button>
                        {!['delivered', 'failed', 'transferred', 'returned'].includes(d.status) && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEditDelivery(d)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setTransferModal({ open: true, deliveryId: d._id })}>
                              <ArrowLeftRight className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleReturn(d._id)}>
                              Буцаах
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {deliveries.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-400">Хүргэлт байхгүй</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); fetchDeliveries(page - 1); }}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-zinc-600">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => { setPage(page + 1); fetchDeliveries(page + 1); }}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Transfer modal */}
      <Dialog open={transferModal.open} onOpenChange={(v) => { if (!v) { setTransferModal({ open: false, deliveryId: null }); setTransferReason(''); setTransferAddress(''); setTransferDistrict(''); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Хүргэлт шилжүүлэх</DialogTitle></DialogHeader>
          <div className="space-y-3 mb-3">
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Хаяг солих (заавал биш)</label>
              <Input placeholder="Шинэ хаяг" value={transferAddress} onChange={e => setTransferAddress(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Дүүрэг солих (заавал биш)</label>
              <Select value={transferDistrict} onChange={e => setTransferDistrict(e.target.value)} className="w-full">
                <SelectOption value="">Дүүрэг сонгох</SelectOption>
                {districts.map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-500 mb-1 block">Шалтгаан (заавал биш)</label>
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-zinc-400"
                placeholder="Шилжүүлэх шалтгаан..." value={transferReason} onChange={e => setTransferReason(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            {drivers.filter(d => d.isActive).map(d => (
              <Button key={d._id} variant="outline" className="w-full justify-start" onClick={() => handleTransfer(d._id)}>
                {d.name} - {d.phone}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit delivery modal */}
      <Dialog open={editModal.open} onOpenChange={(v) => { if (!v) { setEditModal({ open: false, delivery: null }); setEditForm({}); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Хүргэлт засах</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Хүргэлтийн ажилтан</label>
              <Select value={editForm.driver || ''} onChange={e => setEditForm(f => ({ ...f, driver: e.target.value }))}>
                <SelectOption value="">Сонгох</SelectOption>
                {drivers.filter(d => d.isActive).map(d => <SelectOption key={d._id} value={d._id}>{d.name}</SelectOption>)}
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Тэмдэглэл</label>
              <Input value={editForm.note || ''} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Бүс</label>
                <Select value={editForm.zone || ''} onChange={e => setEditForm(f => ({ ...f, zone: e.target.value ? Number(e.target.value) : '' }))}>
                  <SelectOption value="">Сонгох</SelectOption>
                  <SelectOption value="1">1-р бүс</SelectOption>
                  <SelectOption value="2">2-р бүс</SelectOption>
                  <SelectOption value="3">3-р бүс</SelectOption>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Илүү км</label>
                <Input type="number" value={editForm.extraKm || 0} onChange={e => setEditForm(f => ({ ...f, extraKm: Number(e.target.value) }))} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setEditModal({ open: false, delivery: null }); setEditForm({}); }}>Болих</Button>
            <Button onClick={handleEditDelivery}>Хадгалах</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={detailModal.open} onOpenChange={(v) => !v && setDetailModal({ open: false, delivery: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Хүргэлтийн дэлгэрэнгүй</DialogTitle></DialogHeader>
          {detailModal.delivery && (() => {
            const d = detailModal.delivery;
            return (
              <div className="space-y-4 text-sm">
                {/* Статус + огноо */}
                <div className="flex items-center justify-between bg-zinc-50 rounded-lg p-3">
                  <Badge variant={statusColors[d.status]} className="text-sm px-3 py-1">{statusLabels[d.status]}</Badge>
                  <span className="text-xs text-zinc-500">{dayjs(d.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                </div>

                {/* Захиалгын мэдээлэл */}
                <div className="bg-white border rounded-lg divide-y">
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Захиалга №</span>
                    <span className="font-medium">{d.order?.orderNumber}</span>
                  </div>
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Хүлээн авагч</span>
                    <span className="font-medium">{d.order?.receiverName}</span>
                  </div>
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Утас</span>
                    <span className="font-medium">{d.order?.phone1}{d.order?.phone2 ? ` / ${d.order.phone2}` : ''}</span>
                  </div>
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Дүүрэг</span>
                    <span className="font-medium">{d.order?.district?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Хороо</span>
                    <span className="font-medium">{d.order?.khoroo ? `${d.order.khoroo}-р хороо` : '-'}</span>
                  </div>
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Хаяг</span>
                    <span className="font-medium text-right max-w-[60%]">{d.order?.address}</span>
                  </div>
                  {d.order?.boxes?.length > 0 && (
                    <div className="flex justify-between p-3">
                      <span className="text-zinc-500">Ачаа</span>
                      <span className="font-medium text-right max-w-[60%]">
                        {d.order.boxes.map((b, i) => `${b}${d.order.weights?.[i] ? ' - ' + d.order.weights[i] + 'гр' : ''}`).join(', ')}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between p-3">
                    <span className="text-zinc-500">Хүргэлтийн ажилтан</span>
                    <span className="font-medium">{d.driver?.name}</span>
                  </div>
                  {d.zone && (
                    <div className="flex justify-between p-3">
                      <span className="text-zinc-500">Бүсчлэл</span>
                      <span className="font-medium">{d.zone}-р бүс</span>
                    </div>
                  )}
                  {d.extraKm > 0 && (
                    <div className="flex justify-between p-3">
                      <span className="text-zinc-500">Илүү км</span>
                      <span className="font-medium">{d.extraKm} км</span>
                    </div>
                  )}
                  {d.note && (
                    <div className="flex justify-between p-3">
                      <span className="text-zinc-500">Тэмдэглэл</span>
                      <span className="font-medium text-right max-w-[60%]">{d.note}</span>
                    </div>
                  )}
                  {d.failReason && (
                    <div className="flex justify-between p-3">
                      <span className="text-zinc-500">Шалтгаан</span>
                      <span className="font-medium text-red-600 text-right max-w-[60%]">{d.failReason}</span>
                    </div>
                  )}
                </div>

                {/* Төлвийн түүх */}
                {d.statusHistory?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-zinc-700">Төлвийн түүх</h4>
                    <div className="relative border-l-2 border-zinc-200 ml-2 space-y-3">
                      {d.statusHistory.map((h, i) => (
                        <div key={i} className="ml-4 relative">
                          <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-white border-2 border-zinc-400" />
                          <div className="flex items-center gap-2">
                            <Badge variant={statusColors[h.status] || 'default'} className="text-xs">{statusLabels[h.status] || h.status}</Badge>
                            <span className="text-xs text-zinc-400">{dayjs(h.changedAt).format('MM-DD HH:mm')}</span>
                          </div>
                          {h.note && <p className="text-xs text-zinc-500 mt-0.5">{h.note}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Шилжүүлэлтийн түүх */}
                {d.transfers?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 text-zinc-700">Шилжүүлэлтийн түүх</h4>
                    <div className="relative border-l-2 border-orange-200 ml-2 space-y-3">
                      {d.transfers.map((t, i) => (
                        <div key={i} className="ml-4 relative">
                          <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full bg-white border-2 border-orange-400" />
                          <div className="text-sm">
                            <span className="font-medium">{t.fromDriver?.name}</span>
                            <span className="text-zinc-400 mx-1">&rarr;</span>
                            <span className="font-medium">{t.toDriver?.name}</span>
                          </div>
                          <div className="text-xs text-zinc-400">
                            {dayjs(t.transferredAt).format('MM-DD HH:mm')} | {t.transferredBy === 'admin' ? 'Админ' : 'Хүргэлтийн ажилтан'}
                          </div>
                          {t.reason && <p className="text-xs text-zinc-500">Шалтгаан: {t.reason}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Үйлдлүүд */}
                {!['delivered', 'failed', 'transferred'].includes(d.status) && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button className="bg-green-600 hover:bg-green-700"
                      onClick={() => { handleStatusChange(d._id, 'delivered'); setDetailModal({ open: false, delivery: null }); }}>
                      Хүргэгдсэн
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Deliveries;
